<template>
  <div class="st-progress-bar-text-only" aria-hidden="true">
    {{ progressBarLabel }}
  </div>
  <div
    class="st-progress-bar"
    aria-hidden="true"
    :style="{
      width: `calc(${progressBarLength} / var(--base-font-size, 16) * 1rem)`,
    }"
  >
    <span class="st-progress-bar__line" />
    <span
      class="st-progress-bar__progress"
      :style="{
        width: `calc(${progressWidth} / var(--base-font-size, 16) * 1rem)`,
        'max-width': `calc(${progressBarLength}  / var(--base-font-size, 16) * 1rem`,
      }"
    />
    <div
      aria-hidden="true"
      v-for="(step, index) in stepsArray"
      :key="index"
      class="st-progress-bar__step"
      :class="getStepClasses(index)"
      :aria-current="index === props.activeStep - 1 ? 'step' : null"
    >
      <span class="st-progress-bar__step-fill"></span>
      <span class="st-progress-bar__step-indicator"></span>
      <span class="st-progress-bar__step-content">{{ index + 1 }}</span>
    </div>
  </div>
  <span v-if="props.shouldUseSR" class="sr-only" aria-live="polite" aria-atomic="true">
    {{ progressBarLabel }}
  </span>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";

const props = defineProps({
  steps: {
    type: [Array, Number],
    default: 4,
    validator: (value) => {
      // For arrays, must have at least 2 steps
      if (Array.isArray(value)) {
        return value.length >= 2;
      }
      // For numbers, must be at least 2
      return typeof value === "number" && value >= 2;
    },
  },
  activeStep: {
    type: Number,
    default: 1,
  },
  isAriaHidden: {
    type: Boolean,
    default: false,
  },
  shouldUseSR: {
    type: Boolean,
    default: true,
  },
});

// Compute actual steps array based on prop input
const stepsArray = computed(() => {
  if (Array.isArray(props.steps)) {
    return props.steps;
  }
  // Create an array from 1 to the specified number
  return Array.from({ length: props.steps }, (_, i) => i + 1);
});

const progressBarLabel = computed(() => {
  return `Steg ${props.activeStep} av ${stepsArray.value.length}`;
});

// State
const previousActiveStep = ref(props.activeStep);
const direction = ref("forward");
const progressWidth = ref(0);
const initialRender = ref(true);
const hasStepChanged = ref(false);
const progressAnnouncement = ref("");
// The size of two steps (minimum) along with one line between them
// Plus the size of one step and the line between (70) for each subsequent step
const progressBarLength = ref(94 + (props.steps - 2) * 70);

onMounted(() => {
  progressWidth.value = calculateProgressWidth();
  setTimeout(() => {
    initialRender.value = false;
  }, 100);
});

function calculateProgressWidth() {
  // The width of one step + 12 pixels
  const baseProgressWidth = 24 + 12;
  // The width of all steps not counting the first one
  // Plus the width of one step and the line between two steps (70 px)
  const stepPercentage = (props.activeStep - 1) * 70;
  const fullProgressWidth = baseProgressWidth + stepPercentage;

  return fullProgressWidth;
}

function getStepClasses(index) {
  return {
    // Basic state classes
    "st-progress-bar__step--active":
      index === 0 || // First step is always active
      index < props.activeStep ||
      (direction.value === "backward" && index === props.activeStep - 1),
    "st-progress-bar__step--current":
      index === props.activeStep - 1 && index !== 0,
    // Animation classes
    "st-progress-bar__step--animating-forward":
      !initialRender.value &&
      hasStepChanged.value &&
      direction.value === "forward" &&
      index === props.activeStep - 1 &&
      index !== 0,
    "st-progress-bar__step--animating-backward":
      !initialRender.value &&
      hasStepChanged.value &&
      direction.value === "backward" &&
      index === props.activeStep,
    // Visual modifier
    "st-progress-bar__step--last": index === stepsArray.value.length - 1,
  };
}

// Watch for step changes
watch(
  () => props.activeStep,
  (newVal, oldVal) => {
    // Skip animation on initial render
    if (initialRender.value) {
      return;
    }
    // Step change has occurred
    if (newVal !== oldVal) {
      hasStepChanged.value = true;
    }
    direction.value = newVal > oldVal ? "forward" : "backward";
    previousActiveStep.value = oldVal;
    // Calculate new width
    const newWidth = calculateProgressWidth();
    progressWidth.value = newWidth;

    // Force screen reader announcement by briefly clearing and resetting
    if (newVal !== oldVal) {
      const currentAnnouncement = progressBarLabel.value;
      progressAnnouncement.value = "";
      setTimeout(() => {
        progressAnnouncement.value = currentAnnouncement;
      }, 50);
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-progress-bar-text-only {
  display: none;
  text-align: center;
  font-size: rem-calc(16);
  font-weight: 500;
  color: var(--palette-secondary-900);

  @media (max-width: 320px) {
    display: block;
  }
}

.st-progress-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  height: rem-calc(24);

  @media (max-width: 320px) {
    display: none;
  }

  &__line {
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: rem-calc(4);
    background-color: var(--background-color-quaternary);
    transform: translateY(-50%);
    z-index: 1;
    border-radius: rem-calc(2);
  }

  &__progress {
    position: absolute;
    top: 50%;
    height: rem-calc(4);
    background-color: var(--background-color-primary);
    transform: translateY(-50%);
    z-index: 2;
    transition: width 0.4s;
    border-radius: rem-calc(2);
  }

  &__step {
    position: relative;
    z-index: 3;
    width: rem-calc(24);
    height: rem-calc(24);
    border-radius: 50%;
    background-color: var(--background-color-quaternary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: rem-calc(14);
    font-weight: bold;
    color: var(--palette-secondary-900);
    overflow: hidden;
    transition: color 0.2s;

    &-fill {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--background-color-primary);
      transform: translateX(-100%);
      z-index: -1;
    }

    &-content {
      position: relative;
      z-index: 1;
    }

    &--active {
      color: var(--text-color-tertiary);

      .st-progress-bar__step-fill {
        transform: translateX(0);
      }

      .st-progress-bar__step-indicator {
        opacity: 1;
      }
    }

    &--current {
      color: var(--text-color-tertiary);
    }

    &--animating-forward {
      transition-delay: 0.2s; // Text color change delay

      .st-progress-bar__step-fill {
        animation: fillStep 0.1s 0.17s forwards;
      }
    }

    &--animating-backward {
      transition-delay: 0.03s; // Text color change delay

      .st-progress-bar__step-fill {
        animation: unfillStep 0.03s 0.05s backwards;
      }
    }

    &--last {
      .st-progress-bar__step-indicator {
        display: none;
      }
    }
  }

  @keyframes fillStep {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes unfillStep {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
